<template>
  <div class="address-wrapper">
    <p v-if="!value.length" class="empty-address">You haven't filled in the shipping address yet</p>
    <div v-else class="checkout-address-list">
      <button v-for="(item, index) in value" :key="item.id || index" class="checkout-address-card"
        :class="{ selected: selectedIndex === index }" type="button" @click="selectedIndex = index">
        <span class="selected-corner" aria-hidden="true"></span>
        <span class="address-row">
          <span class="address-label">Name:</span>
          <strong>{{ item.name }}</strong>
        </span>
        <span class="address-row">
          <span class="address-label">Phone:</span>
          <strong>{{ item.phone }}</strong>
        </span>
        <span class="address-row">
          <span class="address-label">Address:</span>
          <strong>{{ item.address }}</strong>
        </span>
      </button>
    </div>
    <button class="orange-btn" type="button" @click="dialogVisible = true">Add address</button>

    <el-dialog title="Shipping Address" :visible.sync="dialogVisible" width="550px" :close-on-click-modal="false">
      <div class="address-form">
        <div class="form-row">
          <label>Name</label>
          <el-input v-model="form.name" placeholder="Name" />
        </div>
        <div class="form-row">
          <label>Phone</label>
          <el-input v-model="form.phone" placeholder="Phone" />
        </div>
        <div class="form-row">
          <label>Country</label>
          <el-input v-model="form.country" placeholder="Country" />
        </div>
        <div class="form-row">
          <label>Address</label>
          <el-input v-model="form.detail_address" type="textarea" :rows="3"
            placeholder="State, Street, City, Zip Code" />
        </div>
      </div>
      <span slot="footer">
        <button class="dialog-btn cancel" :disabled="submitting" @click="dialogVisible = false">Cancel</button>
        <button class="dialog-btn confirm" :disabled="submitting" @click="saveAddress">{{ submitting ? 'Saving...' :
          'OK' }}</button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getToken } from '@/util/token-util.js'

export default {
  name: 'AddressDialog',
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: [String, Array],
      default: ''
    }
  },
  data() {
    return {
      dialogVisible: false,
      selectedIndex: 0,
      submitting: false,
      form: {
        name: '',
        phone: '',
        country: '',
        detail_address: ''
      }
    }
  },
  watch: {
    dialogVisible(val) {
      if (!val) {
        this.form = { name: '', phone: '', country: '', detail_address: '' }
      }
    }
  },
  methods: {
    async saveAddress() {
      const { name, phone, country, detail_address } = this.form
      if (!name || !phone || !country || !detail_address) {
        this.$message.error('Please complete all fields')
        return
      }
      if (!getToken()) {
        this.$message.warning('Please log in first')
        this.dialogVisible = false
        return
      }
      this.submitting = true
      try {
        const res = await this.$api({
          url: 'Address/add',
          method: 'post',
          data: {
            name,
            phone,
            country,
            detail_address
          }
        })
        if (res.code !== 200) {
          this.$message.error(res.message || 'Failed to add address')
          return
        }
        const d = res.data || {}
        const nextAddress = {
          id: d.id,
          name: d.name || name,
          phone: d.phone || phone,
          address: [d.country || country, d.detail_address || detail_address]
            .filter(Boolean)
            .join(' ')
        }
        if (Array.isArray(this.value)) {
          this.$emit('input', [...this.value, nextAddress])
          this.selectedIndex = this.value.length
        } else {
          this.$emit('input', nextAddress.address)
        }
        this.$message.success('Address added')
        this.dialogVisible = false
      } catch (e) {
        this.$message.error('Failed to add address')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.empty-address {
  margin: 26px 0 22px;
  color: #111;
  font-size: 16px;
}

.checkout-address-list {
  margin: 16px 0 32px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
}

.checkout-address-card {
  position: relative;
  min-height: 141px;
  padding: 20px 26px;
  overflow: hidden;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #111;
  cursor: pointer;
  text-align: left;
  font-size: 16px;
}

.checkout-address-card.selected {
  border-color: #F34B01;
}

.selected-corner {
  display: none;
}

.checkout-address-card.selected .selected-corner {
  position: absolute;
  top: 0;
  right: 0;
  display: block;
  width: 28px;
  height: 28px;
  background: #F34B01;
  border-bottom-left-radius: 6px;
}

.checkout-address-card.selected .selected-corner::after {
  content: '';
  position: absolute;
  top: 7px;
  right: 7px;
  width: 11px;
  height: 6px;
  border-left: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: rotate(-45deg);
}

.address-row {
  display: grid;
  grid-template-columns: 82px 1fr;
  gap: 26px;
  line-height: 1.2;
}

.address-row+.address-row {
  margin-top: 17px;
}

.address-label {
  color: #3b3b3b;
}

.address-row strong {
  color: #111;
  font-weight: 400;
}

.orange-btn {
  width: 146px;
  height: 46px;
  border: 0;
  border-radius: 4px;
  background: #F34B01;
  color: #fff;
  cursor: pointer;
  font-size: 16px;
}

.address-form .form-row {
  margin-bottom: 18px;
  display: grid;
  grid-template-columns: 100px 1fr;
  align-items: center;
}

.address-form .form-row label {
  text-align: right;
  margin-right: 20px;
  font-size: 14px;
  color: #333;
}

.address-form ::v-deep .el-input__inner {
  outline: none;
}

.address-form ::v-deep .el-input__inner:focus {
  border: 1px solid #F34B01;
  border-color: #F34B01;
}

.dialog-btn {
  height: 34px;
  border: 0;
  border-radius: 4px;
  padding: 0 22px;
  cursor: pointer;
  font-size: 14px;
}

.dialog-btn.confirm {
  background: #F34B01;
  color: #fff;
  margin-left: 12px;
}

.dialog-btn.cancel {
  background: #f0f0f0;
  color: #333;
}

@media (max-width: 960px) {
  .checkout-address-list {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
</style>
